<template>
    <VJoyTemplate full class="activity-report" :class="{isMobileScreen}">
        <template #template-title>
            <div>{{ t('projectclient.activitytracker.navstep.check') }}</div>
        </template>
        <template #template-subtitle>
            <p>{{ startDay }}</p>
        </template>
        <template #template-main>
            <div class="activity-report__body" :class="{isMobileScreen}">
                <div class="activity-report__time-worked" :class="{isMobileScreen}">
                    <SimpleActivityTracker
                        v-if="activityTrackerInformation?.activityType === ActivityTrackerResourceActivityTypeEnum.Simple"
                        :time="activityTrackerInformation?.totalTimeWorked"
                        :unit="activityTrackerInformation?.workedUnit"
                    />
                    <DetailedActivityTracker
                        v-else
                        :weeks-array="weeksArray"
                        :total-time-worked="activityTrackerInformation?.totalTimeWorked"
                        :worked-unit="activityTrackerInformation?.workedUnit"
                    />
                    <p>
                        {{ t('projectclient.activitytracker.more.details.contact') }}
                        <VJoyLink target="_blank" :href="conversationUrl" :text="`${projectInformation?.freelancer?.fullName}`" />
                    </p>

                    <VJoyTextarea
                        v-if="activityTrackerInformation?.workDescription"
                        v-model="activityTrackerInformation!.workDescription"
                        :label="t('projectclient.activitytracker.work.description.label')"
                        disabled
                        :autogrow="true"
                    />
                </div>
                <ProjectSummary :project-information="projectInformation" />
            </div>
        </template>
    </VJoyTemplate>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyLink, VJoyTemplate, VJoyTextarea} from '@maltjoy/core-vue';
    import {ActivityTrackerResourceActivityTypeEnum} from '@api';
    import type {ActivityTrackerResource, ProjectInformationResource} from '@api';
    import {useDivideMonthIntoWeeks, useGetFullMonthNameAndYear} from '../../utils';
    import {ProjectSummary} from '../ProjectSummary';
    import DetailedActivityTracker from './DetailedActivityTracker/DetailedActivityTracker.vue';
    import SimpleActivityTracker from './SimpleActivityTracker.vue';

    const {t} = useTranslation();

    const props = defineProps<{
        activityTrackerInformation: ActivityTrackerResource;
        projectInformation: ProjectInformationResource;
    }>();

    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen} = storeToRefs(screenSizeStore);

    const conversationUrl = computed(() => `/messages/${props.projectInformation?.contract?.conversationId}`);

    const startDay = computed(() => {
        if (props.activityTrackerInformation?.startDate) {
            return useGetFullMonthNameAndYear(props.activityTrackerInformation.startDate);
        }
        return '';
    });

    const weeksArray = computed(() => {
        return props.activityTrackerInformation?.detailedTimeWorked?.workAmounts
            ? useDivideMonthIntoWeeks(props.activityTrackerInformation.detailedTimeWorked.workAmounts)
            : [];
    });
</script>

<style lang="scss" scoped>
    .activity-report {
        padding: 0 var(--joy-core-spacing-4);
        color: var(--joy-color-neutral-60);

        @media (min-width: 1024px) {
            padding: 0;
        }

        :deep(.joy-template-slot-title) {
            font-weight: var(--joy-font-weight-bold) !important;
            font-size: var(--joy-font-size-primary-800) !important;
        }

        :deep(a.joy-link.joy-link_teal) {
            text-decoration: underline;
            font-weight: var(--joy-font-weight-normal);
        }

        :deep(.joy-template__heading) {
            margin-bottom: 0;
        }

        &__body {
            display: flex;
            justify-content: space-between;
            margin-top: var(--joy-core-spacing-5);

            &.isMobileScreen {
                flex-direction: column;
            }
        }

        &__time-worked {
            flex: 6;
            max-width: var(--joy-font-weight-bold);
            margin-right: var(--joy-core-spacing-5);

            &.isMobileScreen {
                margin-right: 0;
            }
        }
    }
</style>
